import { AutocompleteRenderOptionState, Box } from '@mui/material';
import { ChipAutocomplete, PoolButton, PoolModal } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { Tag } from '@synergy/models';
import { useEffect, useState } from 'react';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import * as TagsActions from '../../../../common/actions/tag-actions';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface ChangeEmployeeTagsModalProps {
    tags?: Tag[];
    isVisible?: boolean;
    onConfirm: (tags: Tag[]) => void;
    onCancel: () => void;
}

export const ChangeEmployeeTagsModal = (props: ChangeEmployeeTagsModalProps) => {
    const { tags: propTags, isVisible = false, onConfirm, onCancel } = props;
    const dispatch = useThunkDispatch();
    const availableTags = useAppSelector(state => state.tag.tags);
    const [tags, setTags] = useState<Tag[]>(propTags ?? []);

    useEffect(() => {
        setTags(propTags ?? []);
    }, [propTags]);

    const handleTagsOpen = () => {
        if (availableTags.length === 0) {
            dispatch(TagsActions.fetchTags());
        }
    };

    const handleConfirm = () => {
        onConfirm(tags);
        setTags([]);
    };
    const handleCancel = () => {
        onCancel();
        setTags([]);
    };

    const renderOption = (tag: Tag, state: AutocompleteRenderOptionState) => {
        const nameMatches = match(tag.name, state.inputValue);
        const nameParts = parse(tag.name, nameMatches);

        return (
            <Box>
                {nameParts.map(namePart => (
                    <Box component="span" sx={{ color: namePart.highlight ? 'primary.main' : undefined }}>
                        {namePart.text}
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <PoolModal
            title={<FormattedMessage id="app.contactsAndOrganizations.detail.assignTags" />}
            isVisible={isVisible}
            actionChildren={[
                <PoolButton key="1" variant="text" styleType="firstLevel" onClick={handleCancel}>
                    <FormattedMessage id="app.common.cancel" />
                </PoolButton>,
                <PoolButton key="2" styleType="firstLevel" onClick={handleConfirm}>
                    <FormattedMessage id={'app.common.save'} />
                </PoolButton>,
            ]}
            handleClose={handleCancel}
        >
            <ChipAutocomplete
                noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                options={availableTags}
                getOptionLabel={tag => tag.name}
                renderOption={renderOption}
                getChipLabel={tag => tag.name}
                onChange={(_event, values) => setTags(values)}
                value={tags}
                textfieldPlaceholder="app.common.addTag"
                onOpen={handleTagsOpen}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                limitTags={30}
            />
        </PoolModal>
    );
};
